import { z } from 'zod';

export const LOCALES = ['fr', 'ar', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const LocaleSchema = z.enum(LOCALES);

export const FEATURE_FLAGS = ['onlinePayment', 'smsReminders', 'whatsappReminders', 'sso'] as const;
export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

const pct = z.number().min(0).max(100);
const grade = z.number().min(0).max(20);

/** Assessment weights of a régime, in percent of the course average. `cc` + `exam` must equal 100. */
const RegimeWeightsSchema = z
  .object({ cc: pct, exam: pct })
  .refine((w) => w.cc + w.exam === 100, { message: 'cc + exam must equal 100' });

const TimeSlotSchema = z.object({
  start: z.string().regex(/^\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}$/),
});

/**
 * Everything that varies between institutions. Stored as JSON in `tenant_settings.settings`
 * and always read through `parseTenantSettings` so missing keys fall back to platform defaults.
 */
export const TenantSettingsSchema = z.object({
  locale: z
    .object({
      default: LocaleSchema.default('fr'),
      enabled: z.array(LocaleSchema).min(1).default(['fr', 'ar', 'en']),
      /** IANA zone used for "today" (due dates, lateness, attendance weeks). */
      timeZone: z.string().min(1).default('Africa/Tunis'),
    })
    .prefault({}),
  grading: z
    .object({
      passMark: grade.default(10),
      regimes: z
        .object({
          MIXTE: RegimeWeightsSchema.default({ cc: 30, exam: 70 }),
          CC: RegimeWeightsSchema.default({ cc: 100, exam: 0 }),
          EXAM: RegimeWeightsSchema.default({ cc: 0, exam: 100 }),
        })
        .prefault({}),
      /** Split of the continuous-assessment share between DS and TP, in percent of CC. */
      ccComponents: z
        .object({ DS: pct, TP: pct })
        .refine((c) => c.DS + c.TP === 100, { message: 'DS + TP must equal 100' })
        .default({ DS: 50, TP: 50 }),
      semesterAveraging: z.enum(['credits', 'coefficient']).default('credits'),
      compensation: z
        .object({ enabled: z.boolean().default(true), minAverage: grade.default(10) })
        .prefault({}),
      eliminatoryUeGrade: grade.nullable().default(null),
      retake: z.enum(['replace', 'best']).default('best'),
      absentAtExam: z.enum(['zero', 'missing']).default('zero'),
      decimals: z.number().int().min(0).max(3).default(2),
      /** 'each_level': rounded course/UE averages feed the next level; 'final': full precision, rounded for display only. */
      rounding: z.enum(['each_level', 'final']).default('each_level'),
      autoPublishResults: z.boolean().default(false),
      /**
       * Students may ask for a grade to be checked during this many days after the results of a session are
       * published (« Demande de vérification »). 0 = no requests.
       */
      reviewRequestDays: z.number().int().min(0).max(60).default(7),
      mentions: z
        .array(z.object({ min: grade, key: z.enum(['tres_bien', 'bien', 'assez_bien', 'passable']) }))
        .default([
          { min: 16, key: 'tres_bien' },
          { min: 14, key: 'bien' },
          { min: 12, key: 'assez_bien' },
          { min: 10, key: 'passable' },
        ]),
    })
    .prefault({}),
  attendance: z
    .object({
      /** Unjustified absence hours / ECUE volume. `null` = no elimination. */
      eliminationPct: pct.nullable().default(20),
      alertPct: pct.default(15),
      /** Shown in the monitoring list from this rate on (« Surveillé »). */
      watchPct: pct.default(10),
      justificationDelayHours: z.number().int().min(0).default(48),
      /** Teachers can correct their roll call for this many days after the session. */
      rollCallEditDays: z.number().int().min(0).max(60).default(7),
      /**
       * An ECUE eliminated for absences is not acquired at the principal session and blocks compensation:
       * the student goes to the retake (where the elimination no longer applies). `false` = display only.
       */
      eliminationBarsExam: z.boolean().default(true),
    })
    .prefault({}),
  timetable: z
    .object({
      /** ISO weekday numbers, 1 = Monday. */
      weekdays: z.array(z.number().int().min(1).max(7)).default([1, 2, 3, 4, 5, 6]),
      slots: z.array(TimeSlotSchema).default([
        { start: '08:30', end: '10:00' },
        { start: '10:15', end: '11:45' },
        { start: '12:00', end: '13:30' },
        { start: '14:00', end: '15:30' },
        { start: '15:45', end: '17:15' },
      ]),
    })
    .prefault({}),
  finance: z
    .object({
      applicationFeeMillimes: z.number().int().min(0).default(60_000),
      banks: z.array(z.string()).default(['BIAT', 'STB', 'BNA', 'Attijari bank', 'Amen Bank']),
      /** Receipt numbers: `{prefix}-{YYYY}-{00001}`, one sequence per calendar year. */
      receiptPrefix: z
        .string()
        .regex(/^[A-Z]{1,4}$/)
        .default('R'),
      /** Automatic e-mail reminders, sent once for each delay (days after the due date) that is reached. */
      reminders: z
        .object({
          auto: z.boolean().default(true),
          daysAfterDue: z.array(z.number().int().min(1).max(365)).max(6).default([3, 15, 30]),
        })
        .prefault({}),
    })
    .prefault({}),
  /** Online pre-registration (préinscription). */
  admissions: z
    .object({
      open: z.boolean().default(true),
      /** Application fee shown on the form, paid outside the app (receipt uploaded as a piece). */
      applicationFeeMillimes: z.number().int().min(0).default(60_000),
      requiredPieces: z
        .array(z.enum(['cin', 'bac_transcript', 'bac_certificate', 'photo', 'fee_receipt']))
        .default(['cin', 'bac_transcript', 'bac_certificate', 'photo', 'fee_receipt']),
      /** Working days announced to candidates for the commission's answer. */
      responseDays: z.number().int().min(1).max(60).default(7),
    })
    .prefault({}),
  /** Official documents (transcripts, certificates, minutes). */
  documents: z
    .object({
      /** Printed under « Fait à … ». */
      city: z.string().trim().max(80).nullable().default(null),
      /** Signatory printed on transcripts and certificates; falls back to the issuing user. */
      signatoryName: z.string().trim().max(120).nullable().default(null),
      signatoryTitle: z.string().trim().max(120).nullable().default(null),
    })
    .prefault({}),
  identifiers: z
    .object({
      /** Tokens: {YY} / {YYYY} (academic year start), {PROGRAM} (programme code), {SEQ:n} (zero-padded, must be last). */
      matricule: z
        .string()
        .regex(/^[A-Z0-9{}:-]*\{SEQ:[1-9]\}$/, 'settings.errors.matriculePattern')
        .default('{YY}{PROGRAM}{SEQ:4}'),
    })
    .prefault({}),
  features: z
    .object({
      onlinePayment: z.boolean().default(false),
      smsReminders: z.boolean().default(false),
      whatsappReminders: z.boolean().default(false),
      sso: z.boolean().default(false),
    })
    .prefault({}),
});

export type TenantSettings = z.infer<typeof TenantSettingsSchema>;
export type TenantSettingsInput = z.input<typeof TenantSettingsSchema>;

export function parseTenantSettings(raw: unknown): TenantSettings {
  return TenantSettingsSchema.parse(raw ?? {});
}

export const DEFAULT_TENANT_SETTINGS: TenantSettings = parseTenantSettings({});

/** Effective weights of each assessment kind for a régime, in percent (e.g. MIXTE 40/60 → DS 20, TP 20, EXAM 60). */
export function assessmentWeights(
  grading: TenantSettings['grading'],
  regime: keyof TenantSettings['grading']['regimes'],
): { DS: number; TP: number; EXAM: number } {
  const { cc, exam } = grading.regimes[regime];
  return {
    DS: (cc * grading.ccComponents.DS) / 100,
    TP: (cc * grading.ccComponents.TP) / 100,
    EXAM: exam,
  };
}
